import { useState } from 'react';
import { Folder, ChevronRight, Plus } from 'lucide-react';
import TaskNode from './TaskNode';

export default function ProjectNode({
  project,
  tasks,
  onAddTask,
  onCompleteTask,
  onUncompleteTask,
  isAdmin
}) {
  const [isExpanded, setIsExpanded] = useState(true);

  const activeTasks = tasks.filter(t => !t.completedAt);
  const completedTasks = tasks.filter(t => t.completedAt);
  const totalTasks = tasks.length;
  const completedCount = completedTasks.length;
  const progressPercent = totalTasks > 0 ? Math.round((completedCount / totalTasks) * 100) : 0;

  return (
    <div className="mb-2">
      {/* Project header */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 p-2 rounded-lg bg-surface-elevated border border-border/50
                   cursor-pointer hover:bg-surface-hover transition-colors"
      >
        {/* Expand chevron */}
        <ChevronRight
          size={14}
          className={`text-text-muted flex-shrink-0 transition-transform ${
            isExpanded ? 'rotate-90' : ''
          }`}
        />

        {/* Folder icon */}
        <Folder size={14} className="text-purple-400 flex-shrink-0" />

        {/* Project name */}
        <span className="text-[12px] text-text-primary font-medium flex-1 truncate">
          {project.name}
        </span>

        {/* Task count */}
        <span className="text-[10px] text-text-tertiary">
          {completedCount}/{totalTasks}
        </span>

        {/* Mini progress bar */}
        <div className="w-12 h-1.5 bg-surface-hover rounded-full overflow-hidden">
          <div
            className="h-full bg-purple-500 rounded-full transition-all duration-300"
            style={{ width: `${progressPercent}%` }}
          />
        </div>

        {/* Add task button */}
        {isAdmin && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onAddTask(project.id, null);
            }}
            className="p-1 rounded-md bg-transparent border-none text-accent cursor-pointer
                       hover:bg-accent-muted transition-all"
            title="Add task"
          >
            <Plus size={12} />
          </button>
        )}
      </div>

      {/* Tasks */}
      {isExpanded && (
        <div className="ml-6 mt-1 pl-2 border-l-2 border-border/30">
          {tasks.length === 0 ? (
            <p className="text-[10px] text-text-muted py-2 italic">No tasks yet</p>
          ) : (
            <>
              {/* Active tasks */}
              {activeTasks.map(task => (
                <TaskNode
                  key={task.id}
                  task={task}
                  onComplete={onCompleteTask}
                  onUncomplete={onUncompleteTask}
                />
              ))}

              {/* Separator if both active and completed */}
              {activeTasks.length > 0 && completedTasks.length > 0 && (
                <div className="border-t border-border/30 my-1" />
              )}

              {/* Completed tasks (show first 3) */}
              {completedTasks.slice(0, 3).map(task => (
                <TaskNode
                  key={task.id}
                  task={task}
                  onComplete={onCompleteTask}
                  onUncomplete={onUncompleteTask}
                />
              ))}

              {/* More completed indicator */}
              {completedTasks.length > 3 && (
                <p className="text-[10px] text-text-muted py-1">
                  +{completedTasks.length - 3} more completed
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
